import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  ArrowLeft,
  X,
  Send,
  Pencil,
  Loader2,
  ExternalLink,
  Lightbulb,
  Sparkles,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useState, useEffect, useMemo } from 'react'
import { toast } from '@/components/ui/sonner'
import NotificationBanner from '@/components/NotificationBanner'
import {
  useUpdateMilestoneMetadataMutation,
  useSubmitMilestoneMutation,
  useGetCreativeIdeasQuery,
} from '@/redux/collaborationsApi'
import { SubmissionTimeline } from '@/components/SubmissionTimeline'
import { parseSubmissionData } from '@adthlete/common/utils/submissionDataParser'
import { useCampaignDetail } from '@/hooks/useCampaignDetail'
import { useAuth } from '@/hooks/useAuth'
import SuggestionSheet from '@/components/collaboration-detail/SuggestionSheet'
import AiCreativeAssistantChat from '@/components/collaboration-detail/AiCreativeAssistantChat'

const SubmitCreativeConcept = () => {
  const { id } = useParams<{ id: string }>()

  const navigate = useNavigate()

  const [updateMilestoneMetadata] = useUpdateMilestoneMetadataMutation()
  const [submitMilestone] = useSubmitMilestoneMutation()

  const { campaign, isLoading: detailLoading, refetch } = useCampaignDetail(id)
  const { athlete } = useAuth()

  // Find the "Submit Creative Concept" milestone
  const creativeMilestone = useMemo(() => {
    if (!campaign?.milestones) return null
    return campaign.milestones.find((m) => m.name.toLowerCase().includes('creative concept'))
  }, [campaign?.milestones])

  // Check if there's a submission (pending or approved)
  const activeSubmission = useMemo(() => {
    if (!creativeMilestone?.submissions) return null
    const submission = creativeMilestone.submissions.find(
      (sub) => sub.status === 'pending_review' || sub.status === 'approved',
    )
    if (!submission) return null
    const parsedData = parseSubmissionData(submission.contentDescription)
    const fieldMap = new Map(parsedData.fields.map((f) => [f.label, f.value]))
    const creativeConcept =
      fieldMap.get('Creative Concept') ||
      [
        fieldMap.get('Creative Theme') || fieldMap.get('Content Theme') || '',
        fieldMap.get('Integration Concept') || '',
        fieldMap.get('Brand Talking Points & CTA') || fieldMap.get('Ad Read') || '',
      ]
        .filter(Boolean)
        .join('\n\n') ||
      ''
    return { ...submission, creativeConcept }
  }, [creativeMilestone?.submissions])

  const isApproved = activeSubmission?.status === 'approved'

  const revisionRequested = useMemo(() => {
    if (!creativeMilestone?.submissions) return null
    const submission =
      creativeMilestone.submissions.find((sub) => sub.status === 'revision_requested') ?? null
    if (!submission) return null
    const parsedData = parseSubmissionData(submission.contentDescription)
    const fieldMap = new Map(
      parsedData.fields.map((f: { label: string; value: string }) => [f.label, f.value]),
    )
    const submittedConcept =
      fieldMap.get('Creative Concept') ||
      [
        fieldMap.get('Creative Theme') || fieldMap.get('Content Theme') || '',
        fieldMap.get('Integration Concept') || '',
        fieldMap.get('Brand Talking Points & CTA') || fieldMap.get('Ad Read') || '',
      ]
        .filter(Boolean)
        .join('\n\n') ||
      ''
    const rn = submission.reviewNotes
    const brandFeedback =
      typeof rn === 'object' && rn !== null
        ? rn['Creative Concept'] || rn.additionalComments || rn.feedback || ''
        : String(rn || '')
    return { ...submission, submittedConcept, brandFeedback }
  }, [creativeMilestone?.submissions])

  const isRevisionRequested = creativeMilestone?.status === 'revision_requested'

  const [selectedTheme, setSelectedTheme] = useState('')
  const [isSuggestionSheetOpen, setIsSuggestionSheetOpen] = useState(false)
  const [isAssistantOpen, setIsAssistantOpen] = useState(false)

  // Fetch creative ideas/suggestions from admin
  const { data: creativeIdeasData } = useGetCreativeIdeasQuery(id || '', {
    skip: !id,
  })
  const creativeIdeas = creativeIdeasData?.ideas || []

  const [imgError, setImgError] = useState(false)
  const hasValidLogo =
    campaign?.brand?.logo &&
    typeof campaign.brand.logo === 'string' &&
    campaign.brand.logo.trim().length > 0

  // Load draft from milestone metadata (new single field, with fallback to old selectedTheme)
  useEffect(() => {
    if (creativeMilestone?.metadata) {
      setSelectedTheme(
        creativeMilestone.metadata.creativeConcept ||
          creativeMilestone.metadata.selectedTheme ||
          '',
      )
    }
  }, [creativeMilestone])

  // Auto-open AI assistant when revision is requested
  useEffect(() => {
    if (isRevisionRequested) {
      setIsAssistantOpen(true)
    }
  }, [isRevisionRequested])

  // Show loading state while fetching campaign
  if (detailLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C0FD72]" />
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4">Campaign Not Found</h2>
        <p className="mb-4">The campaign you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-500 hover:underline">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  const handleSubmit = async () => {
    if (!creativeMilestone || !id) {
      toast.error('Error', { description: 'Unable to submit. Please try refreshing the page.' })
      return
    }

    try {
      await submitMilestone({
        campaignId: id,
        milestoneId: creativeMilestone.id,
        submissionData: {
          content_description: JSON.stringify({
            fields: [
              { label: 'Creative Concept', value: selectedTheme, type: 'textarea', order: 1 },
            ],
          }),
          notes: null,
          metadata: null,
        },
      }).unwrap()

      toast('Creative Concept Submitted', {
        description: 'Your concept has been sent to the brand for review.',
      })

      navigate(`/collaborations/${id}`)
    } catch (error) {
      toast.error('Submission Failed', {
        description: 'Failed to submit your creative concept. Please try again.',
      })
    }
  }

  const handleSaveAndSendLater = async () => {
    if (creativeMilestone && id) {
      try {
        await updateMilestoneMetadata({
          campaignId: id,
          milestoneId: creativeMilestone.id,
          metadata: { creativeConcept: selectedTheme },
        }).unwrap()

        toast('Draft Saved', {
          description: 'Your creative concept has been saved. You can submit it later.',
        })
      } catch (error) {
        toast.error('Save Failed', {
          description: 'Failed to save your draft. Please try again.',
        })
      }
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Link
          to={`/collaborations/${id}`}
          className="text-gray-300 hover:text-white inline-flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Collaboration Details
        </Link>

        <Dialog>
          <DialogTrigger asChild>
            <button className="text-[#C0FD72] hover:text-[#C0FD72]/90 inline-flex items-center gap-2 text-sm font-medium">
              View Brand Brief
              <ExternalLink className="w-3 h-3" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-[#1E293B] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Brand Brief</DialogTitle>
              <DialogDescription className="text-gray-400">
                Review the requirements and instructions for this campaign.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <h4 className="font-semibold mb-2 text-[#C0FD72]">Overview</h4>
                <p className="text-gray-200 whitespace-pre-wrap">{campaign.brief}</p>
              </div>

              {campaign.instructions && (
                <div>
                  <h4 className="font-semibold mb-2 text-[#C0FD72]">Instructions</h4>
                  <p className="text-gray-200 whitespace-pre-wrap">{campaign.instructions}</p>
                </div>
              )}

              {campaign.script && (
                <div>
                  <h4 className="font-semibold mb-2 text-[#C0FD72]">Suggested Script</h4>
                  <p className="text-gray-200 italic bg-black/20 p-3 rounded-lg border border-white/5">
                    "{campaign.script}"
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status Banners */}
      {campaign?.conceptStatus === 'submitted' && (
        <div className="mb-6">
          <NotificationBanner
            variant="success"
            message="Creative concept successfully sent to brand for review."
          />
        </div>
      )}

      {campaign?.conceptStatus === 'approved' && (
        <div className="mb-6">
          <NotificationBanner
            variant="success"
            message="Your creative concept has been approved by the brand!"
            action={
              <Button
                onClick={() => navigate(`/collaborations/${id}/upload-review`)}
                className="bg-black text-white hover:bg-black/90 font-semibold"
              >
                Continue to Video Upload
              </Button>
            }
          />
        </div>
      )}

      {/* Show brand feedback when revision is requested */}
      {isRevisionRequested && revisionRequested?.reviewNotes && (
        <div className="mb-6">
          <NotificationBanner
            variant="warning"
            message="The brand has requested changes to your creative concept."
          />
          <div className="mt-3 bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 space-y-2">
            <h4 className="font-semibold text-orange-300 flex items-center gap-2">
              <span>📝</span> Brand Feedback
            </h4>
            {typeof revisionRequested.reviewNotes === 'object' ? (
              <div className="space-y-2 text-white/90 text-sm">
                {/* New single-field format */}
                {revisionRequested.reviewNotes['Creative Concept'] && (
                  <p className="whitespace-pre-wrap">
                    {revisionRequested.reviewNotes['Creative Concept']}
                  </p>
                )}
                {/* Legacy 3-field formats */}
                {!revisionRequested.reviewNotes['Creative Concept'] && (
                  <>
                    {(revisionRequested.reviewNotes.creativeTheme ||
                      revisionRequested.reviewNotes['Creative Theme']) && (
                      <p className="whitespace-pre-wrap">
                        {revisionRequested.reviewNotes.creativeTheme ||
                          revisionRequested.reviewNotes['Creative Theme']}
                      </p>
                    )}
                    {(revisionRequested.reviewNotes.integrationConcept ||
                      revisionRequested.reviewNotes['Integration Concept']) && (
                      <p className="whitespace-pre-wrap">
                        {revisionRequested.reviewNotes.integrationConcept ||
                          revisionRequested.reviewNotes['Integration Concept']}
                      </p>
                    )}
                    {(revisionRequested.reviewNotes.adRead ||
                      revisionRequested.reviewNotes['Brand Talking Points & CTA']) && (
                      <p className="whitespace-pre-wrap">
                        {revisionRequested.reviewNotes.adRead ||
                          revisionRequested.reviewNotes['Brand Talking Points & CTA']}
                      </p>
                    )}
                  </>
                )}
                {revisionRequested.reviewNotes.additionalComments && (
                  <p className="whitespace-pre-wrap">
                    {revisionRequested.reviewNotes.additionalComments}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-white/90 text-sm whitespace-pre-wrap">
                {revisionRequested.reviewNotes as string}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Show revision requested banner even if no notes */}
      {isRevisionRequested && !revisionRequested?.reviewNotes && (
        <div className="mb-6">
          <NotificationBanner
            variant="warning"
            message="The brand has requested changes to your creative concept. Please review and resubmit."
          />
        </div>
      )}

      {/* Suggestion bottom sheet */}
      {isSuggestionSheetOpen && creativeIdeas.length > 0 && (
        <SuggestionSheet
          ideas={creativeIdeas}
          onUseSuggestion={(concept) => setSelectedTheme(concept)}
          onClose={() => setIsSuggestionSheetOpen(false)}
        />
      )}

      {/* Brand Header Card */}
      <div className="mb-6 bg-white/10 backdrop-blur-xs shadow-sm rounded-2xl p-6 border border-white/5">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full overflow-hidden bg-white/10 border border-white/20 shrink-0 flex items-center justify-center">
            {hasValidLogo && !imgError ? (
              <img
                src={campaign.brand.logo}
                alt={campaign.brand.name}
                className="h-full w-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <span className="text-2xl font-bold text-white/50">
                {campaign.brand?.name ? campaign.brand.name.charAt(0).toUpperCase() : 'C'}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-bold text-white">{campaign.brand.name}</h2>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <span>
                {Array.isArray(campaign.platform)
                  ? campaign.platform.join(', ')
                  : campaign.platform}
              </span>
            </div>
            {Array.isArray(campaign.structuredDeliverables) &&
              campaign.structuredDeliverables.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(
                    campaign.structuredDeliverables as Array<{
                      id: string
                      platform: string
                      format: string
                    }>
                  ).map((d) => (
                    <span
                      key={d.id}
                      className="inline-flex items-center text-xs bg-white/8 border border-white/12 rounded-full px-2.5 py-0.5 text-gray-300"
                    >
                      {d.platform} · {d.format}
                    </span>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Show submission view if there's an active submission (pending or approved) */}
      {activeSubmission ? (
        <div className="bg-white/5 backdrop-blur-xs rounded-2xl p-5 sm:p-8 border border-white/5">
          {/* Header */}
          <div className="mb-5 sm:mb-8">
            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-3 ${
                isApproved
                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                  : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
              }`}
            >
              <div
                className={`h-2 w-2 rounded-full shrink-0 ${isApproved ? 'bg-green-400' : 'bg-blue-400 animate-pulse'}`}
              />
              <span className="text-sm font-medium">
                {isApproved ? 'Approved' : 'Pending Review'}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-1.5">
              {isApproved ? 'Creative Concept Approved' : 'Creative Concept Submitted'}
            </h1>
            <p className="text-gray-400 text-sm leading-relaxed">
              {isApproved
                ? 'Your creative concept has been approved. You can now proceed to the next milestone.'
                : 'Submitted and awaiting review from the brand.'}
            </p>
          </div>

          {/* Submission Details */}
          <div className="space-y-4">
            <div className="bg-white/5 rounded-xl p-4 sm:p-5 border border-white/10 space-y-4">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Submitted</p>
                <p className="text-white text-sm">
                  {new Date(activeSubmission.submittedAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              {activeSubmission.creativeConcept && (
                <div className="border-t border-white/8 pt-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">
                    Your concept
                  </p>
                  <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                    {activeSubmission.creativeConcept}
                  </p>
                </div>
              )}
            </div>

            {!isApproved && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <p className="text-yellow-300 text-sm leading-relaxed">
                  <strong>What happens next?</strong> The brand will review your concept and either
                  approve it or request changes. You'll be notified when they decide.
                </p>
              </div>
            )}

            {isApproved && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="text-green-300 text-sm leading-relaxed">
                  <strong>Approved!</strong> Your creative concept has been approved. You can now
                  proceed to the next milestone.
                </p>
              </div>
            )}

            {/* Timeline Section */}
            <div className="bg-white/5 rounded-xl p-4 sm:p-5 border border-white/10">
              <h3 className="text-base font-semibold text-white mb-4">Review Timeline</h3>
              <SubmissionTimeline
                submissionId={activeSubmission.id}
                milestoneId={creativeMilestone.id}
                currentUserRole="athlete"
              />
            </div>
          </div>
        </div>
      ) : (
        /* Single-brief form */
        <div className="space-y-6">
          {/* Heading */}
          <div className="px-1">
            <h1 className="text-2xl font-bold text-white mb-1">Creative Concept</h1>
            <p className="text-gray-400 text-sm leading-relaxed">
              Give the brand a clear direction for your content. A short paragraph is all you need
              -- describe what you will create, how the brand fits in, and the general vibe.
            </p>
          </div>

          {/* Manager / admin suggestions banner */}
          {creativeIdeas.length > 0 && (
            <button
              onClick={() => setIsSuggestionSheetOpen(true)}
              className="w-full bg-white/5 hover:bg-white/8 border border-white/12 rounded-2xl px-4 py-3.5 flex items-center gap-3 group transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                <Lightbulb className="w-4 h-4 text-[#C0FD72]" />
              </div>
              <div className="text-left flex-1">
                <p className="text-white text-sm font-semibold">
                  {creativeIdeas.length === 1
                    ? creativeIdeas[0].createdByTeam
                      ? `The team at ${creativeIdeas[0].createdByTeam} has a concept suggestion`
                      : 'Your manager left you a concept suggestion'
                    : `${creativeIdeas.length} concept suggestions for you`}
                </p>
                <p className="text-gray-500 text-xs mt-0.5">
                  Tap to review — you can use it as-is or edit before submitting
                </p>
              </div>
              <span className="text-gray-500 group-hover:text-[#C0FD72] text-sm transition-colors">
                &rsaquo;
              </span>
            </button>
          )}

          {/* Textarea */}
          <div className="bg-white/5 border border-white/8 rounded-2xl p-5">
            <Textarea
              placeholder="Describe your idea -- e.g. I am planning a training routine TikTok and will bring in the brand naturally during my prep. I will keep it authentic and unscripted, the way my audience expects from me..."
              className="min-h-[180px] bg-transparent border-none shadow-none outline-hidden ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-gray-600 text-base leading-relaxed resize-none p-0"
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
            />
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/8">
              <span className="text-gray-600 text-xs">
                {selectedTheme.length > 0
                  ? `${selectedTheme.length} characters`
                  : 'Start typing or use the AI assistant below'}
              </span>
              {selectedTheme.length > 0 && (
                <button
                  onClick={() => setSelectedTheme('')}
                  className="text-gray-600 hover:text-gray-400 text-xs transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Connect social accounts nudge — shown in parent, never inside chat */}
          {(athlete?.socialAccounts?.length ?? 0) === 0 && (
            <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
              <Sparkles className="w-4 h-4 text-[#C0FD72] shrink-0 mt-0.5" />
              <p className="text-xs text-gray-400 leading-snug">
                <a
                  href="/settings?tab=social"
                  className="text-[#C0FD72] font-medium underline underline-offset-2"
                >
                  Connect your social accounts
                </a>{' '}
                so your AI assistant can match your voice and content style.
              </p>
            </div>
          )}

          {/* AI Assistant */}
          {!isAssistantOpen ? (
            <button
              onClick={() => setIsAssistantOpen(true)}
              className="w-full flex items-center gap-3 bg-[#C0FD72]/8 hover:bg-[#C0FD72]/14 border border-[#C0FD72]/25 rounded-2xl px-5 py-4 transition-all group"
            >
              <div className="w-9 h-9 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/30 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-[#C0FD72]" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-medium">Chat with your AI assistant</p>
                <p className="text-gray-500 text-xs mt-0.5">
                  Describe your idea and I will help you write it up
                </p>
              </div>
              <span className="ml-auto text-[#C0FD72] text-sm group-hover:translate-x-0.5 transition-transform">
                &rarr;
              </span>
            </button>
          ) : (
            <AiCreativeAssistantChat
              brandName={campaign.brand.name}
              revisionContext={
                isRevisionRequested && revisionRequested
                  ? {
                      previousConcept: revisionRequested.submittedConcept,
                      brandFeedback: revisionRequested.brandFeedback,
                    }
                  : undefined
              }
              onApplyConcept={(value) => setSelectedTheme(value)}
              onClose={() => setIsAssistantOpen(false)}
            />
          )}

          {/* Actions */}
          <div className="space-y-3 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={!selectedTheme.trim()}
              variant="lime"
              className="w-full font-semibold h-14 text-base disabled:opacity-40"
            >
              {isRevisionRequested ? 'Resubmit to Brand' : 'Submit to Brand'}
            </Button>
            <Button
              onClick={handleSaveAndSendLater}
              disabled={!selectedTheme.trim()}
              variant="outline"
              className="w-full border-white/20 text-gray-300 hover:bg-white/5 hover:text-white font-medium h-12 text-sm bg-transparent disabled:opacity-30"
            >
              Save draft &amp; finish later
            </Button>
          </div>
        </div>
      )}

      {/* Brand Demo Section - For Testing */}
      {/* TODO: Re-implement brand review actions with proper API integration */}
      {/* {campaign?.conceptStatus === 'submitted' && (
        <div className="mt-8 bg-white/5 backdrop-blur-xs rounded-2xl p-8 border border-white/5">
          <div className="mb-6">
            <h2 className="text-white text-xl font-semibold mb-2">Brand Review Actions</h2>
            <p className="text-gray-400 text-sm">Demo: Brand-side actions for testing workflow</p>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => {
                // TODO: Implement approve action
                toast({
                  title: 'Concept Approved',
                  description: 'The creative concept has been approved by the brand',
                })
              }}
              className="flex-1 bg-[#C0FD72] text-black hover:bg-[#C0FD72]/90 font-medium"
            >
              Approve Concept
            </Button>
            <Button
              onClick={() => {
                // TODO: Implement request changes action
                toast({
                  title: 'Changes Requested',
                  description: 'The brand has requested changes to the concept',
                })
              }}
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              Request Changes
            </Button>
          </div>
        </div>
      )} */}
    </div>
  )
}

export default SubmitCreativeConcept
